import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Trash2, ListChecks } from "lucide-react";
import { toast } from "sonner";

export const CP_CATEGORIES = [
    { v: "regulatory", label: "Regulatory" },
    { v: "third_party_consent", label: "Third-party consent" },
    { v: "financing", label: "Financing" },
    { v: "legal", label: "Legal" },
    { v: "tax", label: "Tax" },
    { v: "other", label: "Other" },
];
const CP_STATUSES = [
    { v: "open", label: "Open" },
    { v: "satisfied", label: "Satisfied" },
    { v: "waived", label: "Waived" },
];

export default function ConditionsTab({ dealId, onChange }) {
    const [data, setData] = useState({ items: [], summary: null });
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState({ title: "", category: "other", owner: "", due_date: "" });

    const refresh = useCallback(async () => {
        const { data } = await api.get(`/conditions-precedent/deal/${dealId}`);
        setData(data);
        setLoading(false);
    }, [dealId]);
    useEffect(() => { refresh(); }, [refresh]);

    // After a mutation, reload the list and let the parent refresh the deal
    // (its tab label and header carry the CP counts). Only called on user
    // actions, never on mount, so there is no render loop.
    const changed = async () => { await refresh(); onChange?.(); };

    const add = async (e) => {
        e.preventDefault();
        if (!draft.title.trim()) return;
        try {
            await api.post(`/conditions-precedent/deal/${dealId}`, {
                title: draft.title.trim(), category: draft.category,
                owner: draft.owner || null, due_date: draft.due_date || null,
            });
            setDraft({ title: "", category: draft.category, owner: "", due_date: "" });
            changed();
        } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    };
    const setStatus = async (id, status) => {
        try { await api.patch(`/conditions-precedent/${id}`, { status }); changed(); }
        catch (err) { toast.error(err.response?.data?.detail || err.message); }
    };
    const del = async (id) => {
        if (!confirm("Delete this condition?")) return;
        await api.delete(`/conditions-precedent/${id}`);
        changed();
    };

    const s = data.summary;
    const today = new Date().toISOString().slice(0, 10);

    return (
        <div className="border border-border rounded-md bg-white p-6 space-y-4" data-testid="conditions-tab">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="font-display font-bold tracking-tight flex items-center gap-2">
                        <ListChecks className="w-4 h-4 text-gold" /> Conditions precedent
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        Status tracker only — not legal advice or drafting. Satisfied and waived both count as resolved.
                    </p>
                </div>
                {s && s.total > 0 && (
                    <div className="text-right" data-testid="cp-summary">
                        <div className="font-mono text-2xl font-bold text-navy">{s.completion_pct}%</div>
                        <div className="text-xs text-muted-foreground">
                            {s.resolved}/{s.total} resolved{s.overdue ? ` · ${s.overdue} overdue` : ""}
                        </div>
                    </div>
                )}
            </div>
            {s && s.total > 0 && (
                <div className="h-1.5 rounded bg-neutral-100 overflow-hidden"><div className="h-full bg-gold" style={{ width: `${s.completion_pct}%` }} /></div>
            )}

            <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-12 gap-2" data-testid="cp-add-form">
                <Input className="md:col-span-5" placeholder="Condition (e.g. Landlord consent to assignment)" value={draft.title}
                    onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} data-testid="cp-title-input" />
                <div className="md:col-span-2">
                    <Select value={draft.category} onValueChange={(v) => setDraft((d) => ({ ...d, category: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{CP_CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <Input className="md:col-span-2" placeholder="Owner" value={draft.owner} onChange={(e) => setDraft((d) => ({ ...d, owner: e.target.value }))} />
                <Input className="md:col-span-2" type="date" value={draft.due_date} onChange={(e) => setDraft((d) => ({ ...d, due_date: e.target.value }))} />
                <Button type="submit" className="md:col-span-1 bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="cp-add-btn"><Plus className="w-4 h-4" /></Button>
            </form>

            {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : data.items.length === 0 ? (
                <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-md">
                    No conditions logged yet.
                </div>
            ) : (
                <table className="w-full text-sm" data-testid="cp-table">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                            <th className="text-left py-2">Condition</th><th className="text-left py-2">Category</th>
                            <th className="text-left py-2">Owner</th><th className="text-left py-2">Due</th>
                            <th className="text-left py-2">Status</th><th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.items.map((c) => {
                            const overdue = c.status === "open" && c.due_date && c.due_date.slice(0, 10) < today;
                            return (
                                <tr key={c.id} className="border-b border-border/60" data-testid={`cp-row-${c.id}`}>
                                    <td className={`py-3 ${c.status !== "open" ? "text-muted-foreground line-through" : ""}`}>{c.title}</td>
                                    <td className="py-3 text-xs">{CP_CATEGORIES.find((x) => x.v === c.category)?.label || c.category}</td>
                                    <td className="py-3 text-xs">{c.owner || "—"}</td>
                                    <td className={`py-3 text-xs font-mono ${overdue ? "text-red-600 font-medium" : "text-muted-foreground"}`}>{c.due_date ? c.due_date.slice(0, 10) : "—"}</td>
                                    <td className="py-3">
                                        <Select value={c.status} onValueChange={(v) => setStatus(c.id, v)}>
                                            <SelectTrigger className="h-8 w-32" data-testid={`cp-status-${c.id}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>{CP_STATUSES.map((x) => <SelectItem key={x.v} value={x.v}>{x.label}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </td>
                                    <td className="py-3 text-right">
                                        <Button size="icon" variant="ghost" onClick={() => del(c.id)}><Trash2 className="w-3.5 h-3.5 text-muted-foreground" /></Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}
